import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import UsersTab from '../components/admin/UsersTab';
import SubscriptionsTab from '../components/admin/SubscriptionsTab';
import DiscountsTab from '../components/admin/DiscountsTab';
import SupportTab from '../components/admin/SupportTab';
import ReferralsTab from '../components/admin/ReferralsTab';
import NotificationsDropdown from '../components/admin/NotificationsDropdown';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function AdminDashboard() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState(null);
  const [scraperHealth, setScraperHealth] = useState(null);
  const [openTickets, setOpenTickets] = useState(0);
  const [clearingEmails, setClearingEmails] = useState(false);

  const clearEmailQueue = async () => {
    if (!window.confirm('Clear ALL pending emails? This cannot be undone.')) return;
    setClearingEmails(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/clear-email-queue`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      toast.success(data.message);
      fetchOverview();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setClearingEmails(false);
    }
  };

  useEffect(() => {
    fetchOverview();
  }, [token]);

  const fetchOverview = async () => {
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      const [statsRes, healthRes, ticketsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/stats`, { headers }),
        fetch(`${API_URL}/api/admin/scraper-health`, { headers }),
        fetch(`${API_URL}/api/admin/support-tickets?status=open`, { headers })
      ]);
      
      if (statsRes.status === 403) {
        setError('Admin access required');
        return;
      }
      
      setStats(await statsRes.json());
      setScraperHealth(await healthRes.json());
      const ticketData = await ticketsRes.json();
      setOpenTickets(ticketData.counts?.open || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleString() : 'N/A';

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Access Denied</h1>
          <p className="text-slate-400 mb-4">Admin privileges required.</p>
          <Button onClick={() => navigate('/dashboard')}>Back to App</Button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'users', label: 'Users' },
    { id: 'subscriptions', label: 'Subscriptions' },
    { id: 'discounts', label: 'Discounts' },
    { id: 'referrals', label: 'Referrals' },
    { id: 'support', label: `Support${openTickets > 0 ? ` (${openTickets})` : ''}` },
  ];

  return (
    <div className="min-h-screen bg-slate-900">
      <div className="bg-slate-800 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <h1 className="text-xl font-bold text-white">Admin Dashboard</h1>
          <div className="flex items-center gap-3">
            <NotificationsDropdown />
            <Button variant="outline" onClick={() => navigate('/dashboard')}>Back to App</Button>
          </div>
        </div>
      </div>

      <div className="bg-slate-800 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 flex gap-1 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 text-sm font-medium whitespace-nowrap ${
                activeTab === tab.id ? 'text-blue-400 border-b-2 border-blue-400' : 'text-slate-400 hover:text-white'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="pt-6">
                  <p className="text-slate-400 text-sm">Total Users</p>
                  <p className="text-2xl font-bold text-white">{stats?.users?.total || 0}</p>
                </CardContent>
              </Card>
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="pt-6">
                  <p className="text-slate-400 text-sm">Active Subscribers</p>
                  <p className="text-2xl font-bold text-green-400">{stats?.users?.active_subscribers || 0}</p>
                </CardContent>
              </Card>
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="pt-6">
                  <p className="text-slate-400 text-sm">Active Alerts</p>
                  <p className="text-2xl font-bold text-blue-400">{stats?.alerts?.active || 0}</p>
                </CardContent>
              </Card>
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="pt-6">
                  <p className="text-slate-400 text-sm">Monthly Revenue</p>
                  <p className="text-2xl font-bold text-yellow-400">£{stats?.total_revenue_estimate || 0}</p>
                </CardContent>
              </Card>
            </div>

            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  Scraper Health
                  <Badge className={
                    scraperHealth?.health_status === 'healthy' ? 'bg-green-500' :
                    scraperHealth?.health_status === 'warning' ? 'bg-yellow-500' : 'bg-red-500'
                  }>
                    {scraperHealth?.health_status || 'Unknown'}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div><p className="text-slate-400">Last Run</p><p className="text-white">{formatDate(scraperHealth?.scraper?.last_run)}</p></div>
                  <div><p className="text-slate-400">Alerts Processed</p><p className="text-white">{scraperHealth?.scraper?.alerts_processed || 0}</p></div>
                  <div><p className="text-slate-400">Total Jobs</p><p className="text-white">{scraperHealth?.totals?.total_jobs_scraped || 0}</p></div>
                  <div>
                    <p className="text-slate-400">Pending Emails</p>
                    <div className="flex items-center gap-2">
                      <p className="text-white">{scraperHealth?.email_queue?.pending || 0}</p>
                      {(scraperHealth?.email_queue?.pending || 0) > 0 && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={clearEmailQueue}
                          disabled={clearingEmails}
                          className="text-red-400 hover:text-red-300 hover:bg-red-900/20 h-6 px-2"
                        >
                          {clearingEmails ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Recent Jobs */}
            <Card className="bg-slate-800 border-slate-700 mt-6">
              <CardHeader>
                <CardTitle className="text-white text-lg">Recent Jobs Scraped</CardTitle>
              </CardHeader>
              <CardContent>
                {scraperHealth?.recent_jobs?.length > 0 ? (
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {scraperHealth.recent_jobs.map((job, idx) => (
                      <div key={idx} className="p-3 bg-slate-900 rounded-lg flex justify-between items-start">
                        <div className="flex-1 min-w-0">
                          <a href={job.job_url} target="_blank" rel="noopener noreferrer" 
                             className="text-white font-medium hover:text-[#00A651] truncate block">
                            {job.title}
                          </a>
                          <p className="text-slate-400 text-sm truncate">{job.employer} • {job.location}</p>
                          <p className="text-slate-500 text-xs">{job.grade}</p>
                        </div>
                        <div className="text-right flex-shrink-0 ml-4">
                          <p className="text-slate-400 text-xs">Scraped</p>
                          <p className="text-white text-sm">{job.scraped_at ? new Date(job.scraped_at).toLocaleDateString() : '-'}</p>
                          <p className="text-slate-500 text-xs">{job.scraped_at ? new Date(job.scraped_at).toLocaleTimeString() : ''}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-400">No jobs scraped yet</p>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'users' && <UsersTab token={token} />}
        {activeTab === 'subscriptions' && <SubscriptionsTab token={token} />}
        {activeTab === 'discounts' && <DiscountsTab token={token} />}
        {activeTab === 'referrals' && <ReferralsTab token={token} />}
        {activeTab === 'support' && <SupportTab token={token} />}
      </div>
    </div>
  );
}

export default AdminDashboard;
